import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './BakeryScene.css';
import CharacterPanel from './CharacterPanel';
import Voice from './Voice';
import WayOn from './WayOn';
import { startSceneTeacherSession, startSceneVoiceSession } from './api';
import { BAKERY_COUNTER } from './scenes';
import { euros } from './text';

/** The role the migration casts Élodie in, and the one the voice session is opened against. */
const BAKER = 'baker';

/** A line of the shopping list, worded from the browser's own translations. */
export function errandLabel(line, t) {
  if (line.kind === 'TAG') {
    return t(`bakery.tag.${line.tag}`);
  }
  const name = t(`bakery.item.${line.itemId}`);
  return line.quantity > 1 ? `${line.quantity} × ${name}` : name;
}

function Errand({ round, t }) {
  return (
    <section className="bakery-errand">
      <h2>{t('bakery.shoppingList')}</h2>
      <ul aria-label={t('bakery.shoppingList')}>
        {round.errand.map((line, index) => (
          <li key={index}>{errandLabel(line, t)}</li>
        ))}
      </ul>

      <h2>{t('bakery.wallet')}</h2>
      <p className="bakery-wallet">
        <span className="bakery-money">{euros(round.remainingEuro)}</span>{' '}
        <small>{t('bakery.of', { budget: euros(round.budgetEuro) })}</small>
      </p>

      <h2>{t('bakery.basket')}</h2>
      <ul aria-label={t('bakery.basket')} className="bakery-basket">
        {round.basket.length === 0 && <li className="bakery-empty">{t('bakery.basketEmpty')}</li>}
        {round.basket.map((line) => (
          <li key={line.itemId}>
            {line.quantity} × {t(`bakery.item.${line.itemId}`)}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** What the errand asks for, read before the student goes in. */
function Briefing({ round }) {
  const { t } = useTranslation();
  return (
    <>
      <h1>{t('bakery.briefing.heading')}</h1>
      <p>{t('bakery.briefing.intro')}</p>
      <Errand round={round} t={t} />
    </>
  );
}

/** Where the drawings of what is on offer are kept, one file per item, named by the server. */
const picture = (item) =>
  `${process.env.PUBLIC_URL}/resources/bakery/${item.picture}.svg`;

/**
 * The counter. Every item is named and priced, because the vocabulary is meant to be learnt by
 * sight; nothing here says what is sold out, because that is what the student has to ask about.
 *
 * The drawings carry no alternative text: the word underneath each of them is the thing to be
 * learnt, and a screen reader that read the picture out first would give it away in English.
 */
function DisplayCase({ catalogue, t }) {
  return (
    <ul className="bakery-case" aria-label={t('bakery.displayCase')}>
      {catalogue.map((item) => (
        <li key={item.id}>
          <img className="bakery-case-picture" src={picture(item)} alt="" />
          <span className="bakery-case-name">{t(`bakery.item.${item.id}`)}</span>
          <span className="bakery-case-price">{euros(item.priceEuro)}</span>
        </li>
      ))}
    </ul>
  );
}

/** What the errand asked for, line by line, and what came back with the student. */
function Result({ round, sheet }) {
  const { t } = useTranslation();
  return (
    <section className="bakery-result">
      <h1>{t('bakery.result.heading')}</h1>
      <ul aria-label={t('bakery.result.heading')}>
        {sheet.lines.map((outcome, index) => (
          <li key={index}>
            <span>{errandLabel(outcome.line, t)}</span>
            <span className={outcome.satisfied ? 'bakery-got' : 'bakery-missed'}>
              {!outcome.satisfied &&
                (outcome.satisfiedBy.length === 0
                  ? t('bakery.result.missed')
                  : t('bakery.result.partly', {
                      bought: outcome.satisfiedBy.length,
                      asked: outcome.line.quantity,
                      items: outcome.satisfiedBy
                        .map((id) => t(`bakery.item.${id}`))
                        .join(', '),
                    }))}
              {outcome.satisfied &&
                (outcome.bySubstitute
                  ? t('bakery.result.substitute', {
                      item: outcome.satisfiedBy
                        .map((id) => t(`bakery.item.${id}`))
                        .join(', '),
                    })
                  : t('bakery.result.gotIt'))}
            </span>
          </li>
        ))}
      </ul>

      {sheet.extras.length > 0 && (
        <p>
          {t('bakery.result.alsoBought')}{' '}
          {sheet.extras.map((id) => t(`bakery.item.${id}`)).join(', ')}
        </p>
      )}

      <p>
        {t('bakery.result.spent', {
          spent: euros(sheet.spentEuro),
          budget: euros(round.budgetEuro),
        })}{' '}
        · {t('bakery.result.left', { left: euros(sheet.remainingEuro) })}
      </p>
      <p className="bakery-tally">
        {t('bakery.result.tally', {
          satisfied: sheet.satisfiedCount,
          total: sheet.lines.length,
        })}{' '}
        · {sheet.perfect ? t('bakery.result.perfect') : t('bakery.result.notQuite')}
      </p>
    </section>
  );
}

/**
 * The counter itself: what is on offer, the conversation held over it, and the errand beside it.
 *
 * The conversation happens here rather than on a screen of its own — a student sent elsewhere to
 * speak can no longer see what they came to buy — and it opens on the way in, because the click
 * that entered the shop is the gesture the microphone prompt hangs off.
 */
function Counter({ round, runId, mayFinish, onFinish, onFrame, onSpeaking }) {
  const { t } = useTranslation();
  // Whether the teacher overlay is above the counter. Memory-only, like the rest of the screen's
  // own business: no record of it survives a reload.
  const [teacherOpen, setTeacherOpen] = useState(false);

  // Closed over the run rather than the round: a basket that moves must not remint the ticket.
  const speakToTheBaker = useCallback(
    () => startSceneVoiceSession(runId, BAKER),
    [runId],
  );
  // The teacher door of the same run. The role says whose conversation the teacher reads and
  // where the exchange is recorded — never who the teacher is.
  const askTheTeacher = useCallback(
    () => startSceneTeacherSession(runId, BAKER),
    [runId],
  );

  /** Down the socket the conversation already holds, with the baker still speaking. */
  const heard = useCallback((frame) => onFrame(frame, true), [onFrame]);

  /** Back from the panel that stands in for her, with nobody speaking. */
  const acted = useCallback((frame) => onFrame(frame, false), [onFrame]);

  return (
    <main className="bakery-shop">
      <div className="bakery-counter">
        <DisplayCase catalogue={round.catalogue} t={t} />
        {/* Hidden, never unmounted, while the teacher is above the counter: unmounting stops
            the session, and the baker is meant to be exactly where the student left her. The
            Voice is told as well as wrapped — the attribute takes her off the screen, the
            prop closes her talk gate. */}
        <div hidden={teacherOpen}>
          <Voice
            openSession={speakToTheBaker}
            onScene={heard}
            onSpeaking={onSpeaking}
            openOnMount
            hidden={teacherOpen}
          />
          <button
            type="button"
            className="bakery-teacher-ask"
            onClick={() => setTeacherOpen(true)}
          >
            {t('chat.askTeacher')}
          </button>
        </div>
      </div>
      {/* One conversation on the screen, two on the wire: the teacher takes the microphone
          and the room, the baker keeps her session and her memory of the visit. The teacher
          answers in the student's own language, so the overlay opens the moment the offer is
          taken up — the click is also what the microphone prompt hangs off. */}
      {teacherOpen && (
        <div className="bakery-teacher" role="dialog" aria-label={t('chat.askTeacher')}>
          <Voice
            openSession={askTheTeacher}
            greeted={false}
            idleCaption={t('bakery.teacher.listening')}
            onClose={() => setTeacherOpen(false)}
            openOnMount
          />
        </div>
      )}
      {/* The shopping list and the way on out of the shop, in one column down the side: the list
          says what the visit is for, and the button under it opens once the baker has settled up.
          Wrapped rather than laid straight into the shop's two columns, which would drop the
          button under the counter instead of under the list it is waiting on. */}
      <div className="bakery-carrying">
        <Errand round={round} t={t} />
        <WayOn mayFinish={mayFinish} onFinish={onFinish} />
      </div>
      {round.characterStub && <CharacterPanel runId={runId} onFrame={acted} />}
    </main>
  );
}

/**
 * The bakery as something the player can host: which scene it draws and which of that scene's
 * fields is its own, how it is named while it is on the screen, the palette it dresses the player
 * in, what it makes of the round it is given, and the three faces it is looked at through.
 *
 * The place is all that is drawn here. Which face is up, the board it is played against and the
 * way out of it are the player's, and so is every frame these screens hand up.
 */
export const bakeryScreen = {
  /** The scene this draws, as the run names it. */
  sceneKey: BAKERY_COUNTER,
  /** The field of that scene carrying its round, which is all these components are ever given. */
  payload: 'bakery',
  title: 'bakery.title',
  subtitle: 'bakery.subtitle',
  enter: 'bakery.briefing.enter',
  palette: 'bakery',
  /** A visit already under way, so a reload comes back to the counter rather than to the door. */
  begun: (round) => round.basket.length > 0 || Boolean(round.result),
  /** A visit the baker has settled up: there is an account to read. */
  over: (round) => Boolean(round.result),
  /**
   * And the same round opens the way out of the scene, because the bakery is one scene and being
   * settled up is the whole of having finished it. Two questions with one answer here; the run
   * still has the last word on whether the scene may be left.
   */
  opensTheWayOut: (round) => Boolean(round.result),
  /**
   * The account of the visit, which rides inside the round: the bakery is one scene, and that
   * scene is the one that settled up.
   */
  sheet: (run, round) => round.result,
  Briefing,
  Stage: Counter,
  Result,
};
